import React from 'react';
import type { OptionType } from '@withjoy/joykit';
import { SelectV1, SpacingStack, ButtonV2 } from '@withjoy/joykit';
import { LayoutShell, ContentHeader, CtaGroup, PrimaryCta, DonationProgressBar, IntegratedGiftCard, SecondaryCta } from '../../components';
import { useGiftAmountController } from './GiftAmount.controller';
import { preventNonNumericalInput } from '@shared/utils/preventNonNumericalInput';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { StyledSelectWrapper, StyledAmountButtonsContainer, getAmountButtonOverrides } from '@apps/registry/guest/components/CheckoutDialog/routes/GiftAmount/GiftAmount.styles';
import { Input } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/Input';
import { FormControl } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/FormControl';
import { GiftAmountSelectors } from './GiftAmount.selectors';
import { CustomItemDataType } from '@graphql/generated-base';
import MustHaveChip from '../../components/MustHaveChip';
import { LoadingExpressCheckoutElement } from '@shared/core/stripe';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { LoadingRegistryCheckoutSettings } from '@apps/registry/common/components/LoadingRegistryCheckoutSettings';

export const GiftAmount = () => {
  const {
    goalAmount,
    amountDonated,
    currencyCode,
    amount,
    formattedAmount,
    suggestedDonation,
    formik,
    setAmount,
    currencySymbol,
    stillNeededOptions,
    registryItem,
    showProgressBar,
    enableJoyCredit,
    onSubmit,
  } = useGiftAmountController();
  const { productData, donationFund, mustHave } = registryItem;

  const isCharity = donationFund?.fundType === CustomItemDataType.charity;
  const routePaths = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const { t } = useTranslation('guestRegistry');
  const { fieldAmountLabel } = t('giveGiftDialog');
  const { giveNow, giveNowWithAmount, additionalPaymentOptions } = t('checkoutDialog', 'screens', 'giftAmount');
  const telemetry = useGuestRegistryTelemetry();
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const checkoutFallback = (
    <PrimaryCta
      {...GiftAmountSelectors.GiveNowButton}
      onClick={() => onSubmit('full')}
    >
      {amount ? giveNow({ amount: formattedAmount }) : giveNowWithAmount()}
    </PrimaryCta>
  );

  // Check if this is a US wedding for showing amount buttons
  const isUSWedding = currencyCode === DEFAULT_CURRENCY && eventInfo?.eventType === 'wedding';
  const isCashFund = !suggestedDonation && donationFund?.fundType !== CustomItemDataType.gift;
  const shouldShowAmountButtons = isUSWedding && isCashFund && Number(goalAmount || 0) >= 100;

  return (
    <LayoutShell {...GiftAmountSelectors.ContainerElement}>
      <LayoutShell.Header mobilePlacement={isCharity ? 'static' : 'float'} />
      <LayoutShell.Body>
        <LayoutShell.MediaPanel>
          <IntegratedGiftCard mobileRenderBehavior="fullBleed" />
        </LayoutShell.MediaPanel>
        <LayoutShell.ContentPanel>
          {mustHave && <MustHaveChip />}
          <ContentHeader>
            <ContentHeader.Hed>{productData.title}</ContentHeader.Hed>
            {donationFund?.description && <ContentHeader.Dek isCollapsible={true}>{donationFund.description}</ContentHeader.Dek>}
          </ContentHeader>
          <SpacingStack
            spacing={6}
            marginTop={6}
          >
            {showProgressBar && (
              <DonationProgressBar
                amountDonated={amountDonated}
                totalAmount={goalAmount || ''}
                currencyCode={currencyCode}
                userContribution={amount || 0}
              />
            )}
            <form>
              <DonationAmountInput
                isDisabled={false}
                fieldAmountLabel={fieldAmountLabel()}
                shouldShowAmountButtons={shouldShowAmountButtons}
                {...{ suggestedDonation, formik, stillNeededOptions, setAmount, preventNonNumericalInput, currencySymbol }}
              />
              <CtaGroup marginTop={7}>
                <LoadingRegistryCheckoutSettings
                  eventId={eventId}
                  fallbackContent={checkoutFallback}
                >
                  <LoadingExpressCheckoutElement
                    onParentClick={event => {
                      if (amount) {
                        telemetry.expressCheckoutClicked({ ...event, source: 'CheckoutDialog_GiftAmount' });
                        history.push(router.buildPath(routePaths.itemCheckout.goToPath(registryItem.id, Math.round(amount * 100).toString())));
                      }
                    }}
                    showFallback={!enableJoyCredit}
                    disablePaymentEvents={true}
                    onConfirm={() => {}}
                    onReady={event => telemetry.expressCheckoutElementReady({ ...event, source: 'CheckoutDialog_GiftAmount' })}
                    options={{ buttonHeight: 50 }}
                    upperContent={
                      <SecondaryCta
                        {...GiftAmountSelectors.GiveNowButton}
                        onClick={() => onSubmit('full')}
                      >
                        {additionalPaymentOptions()}
                      </SecondaryCta>
                    }
                    fallbackContent={checkoutFallback}
                  />
                </LoadingRegistryCheckoutSettings>
              </CtaGroup>
            </form>
          </SpacingStack>
        </LayoutShell.ContentPanel>
      </LayoutShell.Body>
    </LayoutShell>
  );
};

type DonationAmountInputProps = {
  suggestedDonation: number | undefined;
  fieldAmountLabel: string;
  formik: ReturnType<typeof useGiftAmountController>['formik'];
  stillNeededOptions: OptionType[];
  setAmount: (amount?: string | undefined) => void;
  preventNonNumericalInput: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  currencySymbol: string;
  isDisabled: boolean;
  shouldShowAmountButtons: boolean;
};

const DonationAmountInput = ({
  suggestedDonation,
  fieldAmountLabel,
  formik,
  stillNeededOptions,
  setAmount,
  preventNonNumericalInput,
  currencySymbol,
  isDisabled,
  shouldShowAmountButtons,
}: DonationAmountInputProps) => {
  const hasError = !!formik.errors.amount && !!formik.touched.amount;
  const currentAmount = formik.values.amount;
  const telemetry = useGuestRegistryTelemetry();

  const predefinedAmounts = [100, 200, 300, 400, 500, 1000];

  const handleAmountButtonClick = (amount: number) => {
    telemetry.giftAmountButtonInteracted({ amount });
    setAmount(amount.toString());
  };

  return (
    <>
      {suggestedDonation ? (
        <FormControl
          label={fieldAmountLabel}
          isInvalid={hasError}
          error={hasError ? formik.errors.amount : undefined}
        >
          <StyledSelectWrapper>
            <SelectV1
              {...GiftAmountSelectors.AmountInput}
              disabled={isDisabled}
              defaultValue={stillNeededOptions[0]}
              searchable={false}
              options={stillNeededOptions}
              fluid={false}
              onChange={e => setAmount(e?.value)}
            />
          </StyledSelectWrapper>
        </FormControl>
      ) : (
        <>
          {shouldShowAmountButtons && (
            <StyledAmountButtonsContainer
              gap={3}
              marginTop={3}
              marginBottom={5}
            >
              {predefinedAmounts.map(amount => (
                <ButtonV2
                  key={amount}
                  variant="outline"
                  intent="neutral"
                  size="md"
                  onClick={() => handleAmountButtonClick(amount)}
                  overrides={getAmountButtonOverrides(!!(currentAmount && Number(currentAmount) === amount))}
                >
                  {currencySymbol}
                  {amount}
                </ButtonV2>
              ))}
            </StyledAmountButtonsContainer>
          )}
          <FormControl
            label={fieldAmountLabel}
            isInvalid={hasError}
            error={hasError ? formik.errors.amount : undefined}
          >
            <Input
              {...GiftAmountSelectors.AmountInput}
              startElement={currencySymbol}
              isInvalid={hasError}
              {...formik.getFieldProps('amount')}
              type="number"
              onKeyDown={preventNonNumericalInput}
              tabIndex={0}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isDisabled}
            />
          </FormControl>
        </>
      )}
    </>
  );
};
